// Working-estimate edits on the Estimate tab (2026-10-14). A takeoff-driven ("T")
// line is recomputed live from the takeoff and has no row of its own, so the
// estimator's typed Qty / Unit Cost / Sales Burden % / Mark Up % lives in
// EstimateRollupOverrides (db/30), keyed by what the line IS. This module is the
// pure part: the key, applying an override to an already-priced line, and the
// ROM column. Nothing here touches the takeoff or the catalog.

import { computeUnitSellPrice, computeSellMultiplier } from "./estimatePricing";
import { catalogUnitCost } from "./carriedPricing";

export const ROM_INCREMENT = 100;

const num = (v) => (v === null || v === undefined || v === "" ? null : Number.isFinite(Number(v)) ? Number(v) : null);

// ROM = the line Total rounded UP to the next $100 — Excel CEILING(total, 100).
// The total is taken to the cent first so float noise (4400.0000000001) never
// bumps a clean figure up a whole increment. A credit rounds toward zero, as
// Excel does (CEILING(-50, 100) = 0); never "-0".
export function romOf(total, increment = ROM_INCREMENT) {
  const n = num(total);
  if (n === null) return null;
  const cents = Math.round(n * 100) / 100;
  return Math.ceil(cents / increment) * increment + 0;
}

// Stable identity of a takeoff-driven line: catalog entity + alternate + add/credit.
export function overrideKeyOf(r) {
  return `${r.EntityKind}:${r.EntityID}:${r.AlternateID ?? 0}:${(r.TotalQty || 0) < 0 ? "cr" : "add"}`;
}
export const overrideKeyOfRow = (o) => `${o.EntityKind}:${o.EntityID}:${o.AlternateID ?? 0}:${o.IsCredit ? "cr" : "add"}`;

// Rows whose key is shared by more than one line (the same item fed by two
// sources) cannot take an override — it would hit both. Returns the shared keys.
export function ambiguousKeys(rows) {
  const seen = new Map();
  for (const r of rows) if (r.EntityID != null) seen.set(overrideKeyOf(r), (seen.get(overrideKeyOf(r)) || 0) + 1);
  return new Set([...seen].filter(([, n]) => n > 1).map(([k]) => k));
}

export const hasAnyOverride = (ov) => !!ov && [ov.QtyOverride, ov.UnitCostOverride, ov.SalesBurdenPct, ov.MarkupPct, ov.UnitPriceOverride].some(v => v != null);

// Excel's Total column is =ROUND(Qty*UnitPrice,0): whole dollars, half away from zero. Never "-0".
export const lineTotal = (sellValue) => (Number.isFinite(sellValue) ? Math.round(Math.abs(sellValue)) * Math.sign(sellValue) + 0 : sellValue);

// Re-prices a line with the estimator's overrides. ov = { QtyOverride, UnitCostOverride,
// SalesBurdenPct, MarkupPct } (any may be null = inherit). Returns the row with TotalQty /
// DirectCost / SellValue replaced, UnitCostEff, and `overridden` flags. No override = the row as is.
//  - Quantity and unit cost multiply into Total Direct Cost.
//  - A typed burden / markup puts the line on the per-line model (same formula + the catalog row's
//    rounding as computeUnitSellPrice); the other of the two inherits the catalog value, else 0.
//  - A line on the estimate's standard stack keeps its own effective sell multiplier.
export function applyRollupOverride(r, ov, entity, settings) {
  if (!hasAnyOverride(ov)) return r;
  const qty0 = r.TotalQty || 0;
  const unit0 = qty0 !== 0 ? r.DirectCost / qty0 : catalogUnitCost(entity, null, settings);
  const qty = num(ov.QtyOverride) ?? qty0;
  const unitCost = num(ov.UnitCostOverride) ?? unit0;
  const directCost = qty * unitCost;

  const burden = num(ov.SalesBurdenPct);
  const markup = num(ov.MarkupPct);
  const entityPerLine = !!entity && (entity.SalesBurdenPct != null || entity.MarkupPct != null);
  const typedPrice = num(ov.UnitPriceOverride);
  let sellValue, derivedMarkupPct;
  if (typedPrice !== null) {
    // A typed Unit Price prices exactly as typed; the markup shown is back-calculated from it.
    sellValue = qty * typedPrice;
    const b = burden ?? num(entity?.SalesBurdenPct) ?? 0;
    derivedMarkupPct = unitCost !== 0 ? Math.round(((typedPrice / unitCost - 1) * 100 - b) * 100) / 100 : null;
  } else if (burden !== null || markup !== null || entityPerLine) {
    const pricing = {
      SalesBurdenPct: burden ?? entity?.SalesBurdenPct ?? null,
      MarkupPct: markup ?? entity?.MarkupPct ?? null,
      PriceRoundIncrement: entity?.PriceRoundIncrement, PriceRoundOffset: entity?.PriceRoundOffset,
    };
    if (pricing.SalesBurdenPct == null) pricing.SalesBurdenPct = 0;
    sellValue = qty * computeUnitSellPrice(unitCost, pricing, settings);
  } else {
    const mult = r.DirectCost !== 0 ? r.SellValue / r.DirectCost : computeSellMultiplier(settings);
    sellValue = directCost * mult;
  }
  return {
    ...r, TotalQty: qty, DirectCost: directCost, SellValue: sellValue, UnitCostEff: unitCost,
    DerivedMarkupPct: derivedMarkupPct,
    overridden: { unitPrice: typedPrice !== null, qty: num(ov.QtyOverride) !== null, unitCost: num(ov.UnitCostOverride) !== null, burden: burden !== null, markup: markup !== null },
  };
}
